/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { oktaResourceConnectionsReset } from "../funcs/oktaResourceConnectionsReset.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { OktaResourceConnectionServer } from "../models/components/oktaresourceconnectionserver.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ResetOktaResourceConnectionRequest,
  ResetOktaResourceConnectionSecurity,
} from "../models/operations/resetoktaresourceconnection.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type ResetOktaResourceConnectionMutationVariables = {
  request: ResetOktaResourceConnectionRequest;
  security?: ResetOktaResourceConnectionSecurity | undefined;
  options?: RequestOptions;
};

export type ResetOktaResourceConnectionMutationData =
  OktaResourceConnectionServer;

export type ResetOktaResourceConnectionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * reset oktaResourceConnections
 *
 * @remarks
 * Delete the Okta resource connection for this server's upstream so it, and every server sharing that upstream, shows as pending again. Requires org:admin.
 */
export function useResetOktaResourceConnectionMutation(
  options?: MutationHookOptions<
    ResetOktaResourceConnectionMutationData,
    ResetOktaResourceConnectionMutationError,
    ResetOktaResourceConnectionMutationVariables
  >,
): UseMutationResult<
  ResetOktaResourceConnectionMutationData,
  ResetOktaResourceConnectionMutationError,
  ResetOktaResourceConnectionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildResetOktaResourceConnectionMutation(client, options),
    ...options,
  });
}

export function mutationKeyResetOktaResourceConnection(): MutationKey {
  return ["@gram/client", "oktaResourceConnections", "reset"];
}

export function buildResetOktaResourceConnectionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: ResetOktaResourceConnectionMutationVariables,
  ) => Promise<ResetOktaResourceConnectionMutationData>;
} {
  return {
    mutationKey: mutationKeyResetOktaResourceConnection(),
    mutationFn: function resetOktaResourceConnectionMutationFn({
      request,
      security,
      options,
    }): Promise<ResetOktaResourceConnectionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(oktaResourceConnectionsReset(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
